import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import { createApiClient } from "@/config/createApiClient";
import { selectedClientServiceIdAtom } from "@/store";
import {
  type OrderItemAddDeliveryLabelCommand,
  OrderItemApi,
  type OrderItemCreateCommand,
  type OrderItemDeliveryLabelPatchCommand,
  type OrderItemMarkOrderItemAsDeliveredCommand,
  type OrderItemUpdateCommand,
} from "../../config/apiClient";
import { useWithTrackingMutation } from "./useWithTrackingMutation";

const apiClient = createApiClient(OrderItemApi);

export const useOrderItems = () => {
  const selectedClientServiceIdAtomValue = useAtomValue(
    selectedClientServiceIdAtom
  );

  const queryClient = useQueryClient();

  const useGetOrderItem = (params: {
    orderId: string;
    orderItemId: string;
    enabled?: boolean;
  }) =>
    useQuery({
      queryKey: ["orderItem", params.orderId, params.orderItemId],
      enabled:
        selectedClientServiceIdAtomValue > 0 &&
        (params.enabled === undefined || params.enabled === true),
      queryFn: () => {
        return apiClient.orderItemsControllerGetOrderItem({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderId: params.orderId,
          orderItemId: params.orderItemId,
        });
      },
    });

  const useCreateOrderItem = () =>
    useWithTrackingMutation<
      { orderId?: string; orderItemCreateCommand: OrderItemCreateCommand },
      Awaited<ReturnType<typeof apiClient.orderItemsControllerCreateOrderItem>>
    >(
      (params: {
        orderId?: string;
        orderItemCreateCommand: OrderItemCreateCommand;
      }) => {
        return params.orderId
          ? apiClient.orderItemsControllerCreateOrderOrderItem({
              clientServiceId: selectedClientServiceIdAtomValue,
              orderId: params.orderId,
              orderItemCreateCommand: params.orderItemCreateCommand,
            })
          : apiClient.orderItemsControllerCreateOrderItem({
              clientServiceId: selectedClientServiceIdAtomValue,
              orderItemCreateCommand: params.orderItemCreateCommand,
            });
      },
      [
        {
          name: "create_order_item",
          getMetaData: (res) => ({
            order_item_id: res.data.id,
            order_id: res.data.orderId,
          }),
        },
        {
          name: "add_product_to_order",
          getMetaData: (res) => ({
            order_id: res.data.orderId,
          }),
        },
        {
          name: "product_selection",
          getMetaData: (res, data) => ({
            order_id: res.data.orderId,
            order_item_id: res.data.id,
            product_id: data.orderItemCreateCommand.catalogMediumId,
            product_name: data.orderItemCreateCommand.name ?? null,
          }),
        },
      ],
      {
        mutationKey: ["createOrderItem"],
        onSuccess: (_, params) =>
          Promise.all([
            queryClient.invalidateQueries({ queryKey: ["orders"] }),
            queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
            queryClient.invalidateQueries({ queryKey: ["worklist"] }),
            queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
            queryClient.invalidateQueries({ queryKey: ["addresses"] }),
          ]),
      }
    );

  const useUpdateOrderItem = () =>
    useWithTrackingMutation<
      {
        orderId: string;
        orderItemId: string;
        orderItemUpdateCommand: OrderItemUpdateCommand;
        limitedRefetch?: boolean;
      },
      Awaited<ReturnType<typeof apiClient.orderItemsControllerUpdateOrderItem>>
    >(
      (params) =>
        apiClient.orderItemsControllerUpdateOrderItem({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderId: params.orderId,
          orderItemUpdateCommand: params.orderItemUpdateCommand,
          orderItemId: params.orderItemId,
        }),
      [
        {
          name: "update_order_item",
          getMetaData: (res) => ({
            order_item_id: res.data.id,
            order_id: res.data.orderId,
          }),
        },
      ],
      {
        mutationKey: ["updateOrderItem"],
        onSuccess: (_, params) =>
          Promise.all([
            queryClient.invalidateQueries({ queryKey: ["orders"] }),
            queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
            queryClient.refetchQueries({
              queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
            }),
            queryClient.refetchQueries({
              queryKey: ["orderItem", params.orderId, params.orderItemId],
            }),
            queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
            !params.limitedRefetch &&
              queryClient.invalidateQueries({ queryKey: ["worklist"] }),
            !params.limitedRefetch &&
              queryClient.invalidateQueries({ queryKey: ["addresses"] }),
          ]),
      }
    );

  const useDeleteOrderItem = () =>
    useWithTrackingMutation<
      { orderItemId: string; orderId: string },
      Awaited<ReturnType<typeof apiClient.orderItemsControllerDeleteOrderItem>>
    >(
      (params) =>
        apiClient.orderItemsControllerDeleteOrderItem({
          orderItemId: params.orderItemId,
          clientServiceId: selectedClientServiceIdAtomValue,
          orderId: params.orderId,
        }),
      [
        {
          name: "delete_order_item",
          getMetaData: (res) => ({
            order_item_id: res.data.id,
            order_id: res.data.orderId,
          }),
        },
      ],
      {
        mutationKey: ["deleteOrderItem"],
        onSuccess: (_, params) =>
          Promise.all([
            queryClient.invalidateQueries({ queryKey: ["orders"] }),
            queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
            queryClient.invalidateQueries({
              queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
            }),
            queryClient.invalidateQueries({
              queryKey: ["orderItem", params.orderId, params.orderItemId],
            }),
            queryClient.invalidateQueries({ queryKey: ["worklist"] }),
            queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
          ]),
      }
    );

  const useCancelOrderItem = () =>
    useWithTrackingMutation<
      { orderItemId: string; orderId: string },
      Awaited<ReturnType<typeof apiClient.orderItemsControllerCancelOrderItem>>
    >(
      (params) =>
        apiClient.orderItemsControllerCancelOrderItem({
          orderItemId: params.orderItemId,
          clientServiceId: selectedClientServiceIdAtomValue,
          orderId: params.orderId,
        }),
      [
        {
          name: "cancel_order_item",
          getMetaData: (res) => ({
            order_item_id: res.data.id,
            order_id: res.data.orderId,
          }),
        },
      ],
      {
        mutationKey: ["cancelOrderItem"],
        onSuccess: (_, params) =>
          Promise.all([
            queryClient.invalidateQueries({ queryKey: ["orders"] }),
            queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
            queryClient.refetchQueries({
              queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
            }),
            queryClient.refetchQueries({
              queryKey: ["orderItem", params.orderId, params.orderItemId],
            }),
            queryClient.invalidateQueries({ queryKey: ["worklist"] }),
            queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
          ]),
      }
    );

  const useGetOrderItemLogs = (params: {
    orderId: string;
    orderItemId: string;
  }) =>
    useQuery({
      queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
      // enabled: selectedClientServiceIdAtomValue > 0,
      queryFn: () => {
        return apiClient.orderItemsControllerGetOrderItemLogs({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderId: params.orderId,
          orderItemId: params.orderItemId,
        });
      },
    });

  const useMarkOrderItemAsDelivered = () =>
    useWithTrackingMutation<
      {
        orderItemId: string;
        orderId: string;
        orderItemMarkAsDeliveredCommand: OrderItemMarkOrderItemAsDeliveredCommand;
      },
      Awaited<
        ReturnType<
          typeof apiClient.orderItemsControllerMarkOrderItemAsDelivered
        >
      >
    >(
      (params) =>
        apiClient.orderItemsControllerMarkOrderItemAsDelivered({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderId: params.orderId,
          orderItemId: params.orderItemId,
          orderItemMarkOrderItemAsDeliveredCommand:
            params.orderItemMarkAsDeliveredCommand,
        }),
      [
        {
          name: "mark_order_item_as_delivered",
          getMetaData: (res) => ({
            order_item_id: res.data.id,
            order_id: res.data.orderId,
          }),
        },
      ],
      {
        mutationKey: ["markOrderItemAsDelivered"],
        onSuccess: (_, params) =>
          Promise.all([
            queryClient.invalidateQueries({ queryKey: ["orders"] }),
            queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
            queryClient.refetchQueries({
              queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
            }),
            queryClient.refetchQueries({
              queryKey: ["orderItem", params.orderId, params.orderItemId],
            }),
            queryClient.invalidateQueries({ queryKey: ["worklist"] }),
            queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
          ]),
      }
    );

  const useDownloadAllOrderItemHDFiles = ({
    orderId,
    orderItemId,
  }: {
    orderId: string;
    orderItemId: string;
  }) => {
    return useQuery({
      queryKey: [
        "downloadAllOrderItemHDFiles",
        selectedClientServiceIdAtomValue,
        orderId,
        orderItemId,
      ],
      queryFn: () =>
        apiClient.orderItemsControllerDownloadAllDesigns(
          {
            orderItemId,
            clientServiceId: selectedClientServiceIdAtomValue,
            orderId,
          },
          {
            responseType: "blob",
          }
        ),
      enabled: false,
    });
  };

  const useMarkDraftOrderItemAsInProgress = () =>
    useWithTrackingMutation<
      {
        orderItemId: string;
        orderId: string;
      },
      Awaited<
        ReturnType<
          typeof apiClient.orderItemsControllerMarkDraftOrderItemAsInProgress
        >
      >
    >(
      (params) =>
        apiClient.orderItemsControllerMarkDraftOrderItemAsInProgress({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderId: params.orderId,
          orderItemId: params.orderItemId,
        }),
      [
        {
          name: "mark_draft_order_item_as_in_progress",
          getMetaData: (res) => ({
            order_item_id: res.data.id,
            order_id: res.data.orderId,
          }),
        },
      ],
      {
        mutationKey: ["markDraftOrderItemAsInProgress"],
        onSuccess: (_, params) =>
          Promise.all([
            queryClient.invalidateQueries({ queryKey: ["orders"] }),
            queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
            queryClient.invalidateQueries({
              queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
            }),
            queryClient.refetchQueries({
              queryKey: ["orderItem", params.orderId, params.orderItemId],
            }),
            queryClient.invalidateQueries({ queryKey: ["worklist"] }),
            queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
          ]),
      }
    );

  const useGetAffectedOrderItemsByQuoteCancellation = (params: {
    isEnabled: boolean;
    orderItemId: string;
    orderId: string;
  }) =>
    useQuery({
      enabled: params.isEnabled,
      queryKey: [
        "affectedOrderItemsByQuoteCancellation",
        params.orderItemId,
        params.orderId,
        selectedClientServiceIdAtomValue,
      ],
      queryFn: () =>
        apiClient.orderItemsControllerGetAffectedOrderItemsByQuoteCancellation({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderItemId: params.orderItemId,
          orderId: params.orderId,
        }),
    });

  const useAddOrderItemDeliveryLabel = () =>
    useWithTrackingMutation<
      {
        orderItemId: string;
        orderId: string;
        orderItemAddDeliveryLabelCommand: OrderItemAddDeliveryLabelCommand;
      },
      Awaited<
        ReturnType<
          typeof apiClient.orderItemDeliveryLabelsControllerAddOrderItemDeliveryLabel
        >
      >
    >(
      (params) =>
        apiClient.orderItemDeliveryLabelsControllerAddOrderItemDeliveryLabel({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderId: params.orderId,
          orderItemId: params.orderItemId,
          orderItemAddDeliveryLabelCommand:
            params.orderItemAddDeliveryLabelCommand,
        }),
      [
        {
          name: "add_delivery_label",
          getMetaData: (_, params) => ({
            order_item_id: params.orderItemId,
            order_id: params.orderId,
          }),
        },
      ],
      {
        mutationKey: ["addDeliveryLabel"],
        onSuccess: (_, params) =>
          Promise.all([
            queryClient.invalidateQueries({ queryKey: ["orders"] }),
            queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
            queryClient.refetchQueries({
              queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
            }),
            queryClient.refetchQueries({
              queryKey: ["orderItem", params.orderId, params.orderItemId],
            }),
            queryClient.invalidateQueries({ queryKey: ["worklist"] }),
            queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
          ]),
      }
    );

  const useValidateOrderItemDeliveryLabel = () =>
    useWithTrackingMutation<
      {
        orderItemId: string;
        orderId: string;
        orderItemDeliveryLabelPatchCommand: OrderItemDeliveryLabelPatchCommand;
      },
      Awaited<
        ReturnType<
          typeof apiClient.orderItemDeliveryLabelsControllerPatchOrderItemDeliveryLabel
        >
      >
    >(
      (params) =>
        apiClient.orderItemDeliveryLabelsControllerPatchOrderItemDeliveryLabel({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderId: params.orderId,
          orderItemId: params.orderItemId,
          orderItemDeliveryLabelPatchCommand:
            params.orderItemDeliveryLabelPatchCommand,
        }),
      [
        {
          name: "validate_delivery_label",
          getMetaData: (_, params) => ({
            order_item_id: params.orderItemId,
            order_id: params.orderId,
          }),
        },
      ],
      {
        mutationKey: ["validateDeliveryLabel"],
        onSuccess: (_, params) =>
          Promise.all([
            queryClient.invalidateQueries({ queryKey: ["orders"] }),
            queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
            queryClient.refetchQueries({
              queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
            }),
            queryClient.refetchQueries({
              queryKey: ["orderItem", params.orderId, params.orderItemId],
            }),
            queryClient.invalidateQueries({ queryKey: ["worklist"] }),
            queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
          ]),
      }
    );

  return {
    useUpdateOrderItem,
    useCancelOrderItem,
    useCreateOrderItem,
    useGetOrderItemLogs,
    useGetOrderItem,
    useMarkOrderItemAsDelivered,
    useMarkDraftOrderItemAsInProgress,
    useDownloadAllOrderItemHDFiles,
    useDeleteOrderItem,
    useGetAffectedOrderItemsByQuoteCancellation,
    useAddOrderItemDeliveryLabel,
    useValidateOrderItemDeliveryLabel,
  };
};
